import { useCallback, useSyncExternalStore } from "react";

// ─── Avisos de escritorio del helpdesk: preferencia y sonido ─────────────────
// Store de módulo (no Context) porque lo leen dos lugares que no comparten
// árbol de props: el hook que avisa (useDesktopNotifications, dentro de la
// bandeja) y el interruptor del menú "Más acciones" del detalle. La
// preferencia vive en localStorage del navegador del agente; si el storage no
// está disponible (modo privado, bloqueado) queda en memoria y apagada.

const STORAGE_KEY = "helpdesk.desktopNotifications";
const listeners = new Set();
let enabled = readStored();
let audioCtx = null;

function readStored() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "on";
  } catch {
    return false;
  }
}

function writeStored(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? "on" : "off");
  } catch {
    // Sin storage: la preferencia dura lo que la pestaña.
  }
}

function emit() {
  listeners.forEach((fn) => fn());
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function notificationsSupported() {
  return typeof window !== "undefined" && typeof window.Notification === "function";
}

export function notificationPermission() {
  return notificationsSupported() ? window.Notification.permission : "unsupported";
}

/** ¿Hay que avisar? Preferencia encendida Y permiso del navegador concedido. */
export function desktopNotificationsActive() {
  return enabled && notificationPermission() === "granted";
}

// El AudioContext se crea (o se reanuda) dentro del clic que enciende los
// avisos: los navegadores lo dejan "suspended" si nace fuera de un gesto del
// usuario, y el primer mensaje llegaría mudo.
function primeAudio() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
  } catch {
    audioCtx = null;
  }
}

/** Dos tonos cortos y suaves (~250 ms), sin archivo de audio. */
export function playNotificationSound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
    const now = audioCtx.currentTime;
    [
      { freq: 880, at: 0 },
      { freq: 660, at: 0.13 },
    ].forEach(({ freq, at }) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.06, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.12);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.13);
    });
  } catch {
    // Sin WebAudio el aviso visual alcanza.
  }
}

/**
 * Enciende o apaga los avisos. Al encender pide el permiso del navegador (debe
 * llamarse desde un clic) y solo queda encendido si se concede.
 * @returns {Promise<'on'|'off'|'denied'|'unsupported'>}
 */
export async function setDesktopNotificationsEnabled(next) {
  if (!next) {
    enabled = false;
    writeStored(false);
    emit();
    return "off";
  }
  if (!notificationsSupported()) return "unsupported";
  primeAudio();
  let permission = window.Notification.permission;
  if (permission === "default") {
    try {
      permission = await window.Notification.requestPermission();
    } catch {
      permission = "denied";
    }
  }
  if (permission !== "granted") {
    enabled = false;
    writeStored(false);
    emit();
    return "denied";
  }
  enabled = true;
  writeStored(true);
  emit();
  return "on";
}

/** Solo para tests. */
export function resetDesktopNotificationsPreference() {
  enabled = readStored();
  audioCtx = null;
  emit();
}

/**
 * @returns {{enabled:boolean, supported:boolean, setEnabled:(v:boolean)=>Promise<string>}}
 *   enabled = preferencia encendida y permiso concedido.
 */
export function useDesktopNotificationsPreference() {
  const active = useSyncExternalStore(subscribe, desktopNotificationsActive, () => false);
  const setEnabled = useCallback((v) => setDesktopNotificationsEnabled(v), []);
  return { enabled: active, supported: notificationsSupported(), setEnabled };
}
